// src/components/admin/TorreDeControl/modals/LocationModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Navigation, Search, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Lima districts organized by zone
const DISTRICTS = {
  "Sur": ["Chorrillos", "San Juan de Miraflores", "Villa El Salvador", "Villa Maria del Triunfo", "Barranco", "Surquillo"],
  "Centro": ["San Isidro", "San Borja", "Miraflores", "Santiago de Surco", "Lince", "Jesús María", "Magdalena del Mar", "San Miguel", "Pueblo Libre", "Breña", "Cercado de Lima", "La Victoria"],
  "Este": ["Ate", "Santa Anita", "El Agustino", "Lurigancho", "La Molina", "San Juan de Lurigancho"],
  "Norte": ["Comas", "Independencia", "San Martin de Porres", "Los Olivos", "Rimac", "Puente Piedra", "Carabayllo", "Ancon", "Santa Rosa"],
  "Callao": ["Callao", "Bellavista", "Carmen de la Legua", "La Perla", "La Punta", "Ventanilla"]
};

// Flatten districts for dropdown
const ALL_DISTRICTS = Object.values(DISTRICTS).flat().sort();

function LocationModal({ item, itemType, onClose, onUpdate }) {
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [district, setDistrict] = useState('');
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isUpdatingDriver, setIsUpdatingDriver] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const leafletLoadedRef = useRef(false);

  // Initialize map center - Lima, Peru default
  const getInitialCenter = () => {
    if (itemType === 'delivery') {
      if (item.latitude && item.longitude) {
        return { lat: item.latitude, lng: item.longitude };
      }
    } else {
      if (item.address?.latitude && item.address?.longitude) {
        return { lat: item.address.latitude, lng: item.address.longitude };
      }
    }
    return { lat: -12.0464, lng: -77.0428 }; // Lima default
  };

  const initialCenter = getInitialCenter();

  // Initialize form data from item
  useEffect(() => {
    if (item) {
      if (itemType === 'delivery') {
        setAddress(item.address || '');
        setDistrict(item.district || '');
        if (item.latitude && item.longitude) {
          setPosition({ lat: item.latitude, lng: item.longitude });
          setManualLat(item.latitude.toString());
          setManualLng(item.longitude.toString());
        }
      } else {
        setAddress(item.address?.address || '');
        setDistrict(item.address?.district || '');
        if (item.address?.latitude && item.address?.longitude) {
          setPosition({ lat: item.address.latitude, lng: item.address.longitude });
          setManualLat(item.address.latitude.toString());
          setManualLng(item.address.longitude.toString());
        }
      }
    }
  }, [item, itemType]);

  // Load Leaflet and initialize map
  useEffect(() => {
    let linkElement = null;
    let scriptElement = null;

    const loadLeaflet = () => {
      // Check if Leaflet is already loaded globally
      if (window.L && !mapInstanceRef.current) {
        initializeMap();
        return;
      }

      // Only load if not already loaded
      if (leafletLoadedRef.current) {
        if (window.L && !mapInstanceRef.current) {
          initializeMap();
        }
        return;
      }

      // Load Leaflet CSS
      linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      linkElement.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      linkElement.crossOrigin = '';
      document.head.appendChild(linkElement);

      // Load Leaflet JS
      scriptElement = document.createElement('script');
      scriptElement.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      scriptElement.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      scriptElement.crossOrigin = '';
      
      scriptElement.onload = () => {
        leafletLoadedRef.current = true;
        setTimeout(() => {
          if (window.L && mapRef.current && !mapInstanceRef.current) {
            initializeMap();
          }
        }, 100);
      };
      
      document.head.appendChild(scriptElement);
    };

    loadLeaflet();

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (markerRef.current && mapInstanceRef.current) {
        try {
          mapInstanceRef.current.removeLayer(markerRef.current);
        } catch (e) {
          console.log('Error removing marker:', e);
        }
      }

      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        } catch (e) {
          console.log('Error removing map:', e);
        }
      }

      if (linkElement && document.head.contains(linkElement)) {
        document.head.removeChild(linkElement);
      }
      if (scriptElement && document.head.contains(scriptElement)) {
        document.head.removeChild(scriptElement);
      }
    };
  }, []);

  const initializeMap = () => {
    if (!window.L || !mapRef.current || mapInstanceRef.current) {
      console.log('Map initialization skipped:', {
        hasLeaflet: !!window.L,
        hasMapRef: !!mapRef.current,
        hasMapInstance: !!mapInstanceRef.current
      });
      return;
    }

    try {
      const L = window.L;

      if (mapRef.current) {
        mapRef.current.innerHTML = '';
        mapRef.current._leaflet_id = null;
      }

      const map = L.map(mapRef.current, {
        center: [initialCenter.lat, initialCenter.lng],
        zoom: 13,
        zoomControl: false,
        attributionControl: true
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);

      mapInstanceRef.current = map;
      setMapReady(true);

      if (position) {
        addMarker(position.lat, position.lng);
      }

      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        setPosition({ lat, lng });
        setManualLat(lat.toFixed(6));
        setManualLng(lng.toFixed(6));
        addMarker(lat, lng);
      });

      console.log('Map initialized successfully');
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  const addMarker = (lat, lng) => {
    if (!mapInstanceRef.current || !window.L) {
      console.log('Cannot add marker: map not ready');
      return;
    }

    const L = window.L;

    try {
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
      }

      markerRef.current = L.marker([lat, lng]).addTo(mapInstanceRef.current);
      mapInstanceRef.current.setView([lat, lng], mapInstanceRef.current.getZoom());
    } catch (error) {
      console.error('Error adding marker:', error);
    }
  };

  // Search address using Google Places Autocomplete
  const fetchAutocompleteSuggestions = async (input) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error('Google Maps API not loaded');
      setSuggestions([]);
      return;
    }

    setSearchLoading(true);

    try {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      
      autocompleteService.getPlacePredictions(
        {
          input: input.trim(),
          componentRestrictions: { country: 'pe' },
          location: new window.google.maps.LatLng(-12.0464, -77.0428),
          radius: 50000,
        },
        (predictions, status) => {
          setSearchLoading(false);
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
      setSuggestions([]);
      setSearchLoading(false);
    }
  };

  const handleAddressChange = (value) => {
    setAddress(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchAutocompleteSuggestions(value);
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    setAddress(suggestion.description);
    setSuggestions([]);
    setSearchLoading(true);

    if (!window.google || !window.google.maps) {
      console.error('Google Maps API not loaded');
      alert('Error: Google Maps no está disponible');
      setSearchLoading(false);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
      setSearchLoading(false);
      
      if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
        const location = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        
        setPosition(location);
        setManualLat(location.lat.toFixed(6));
        setManualLng(location.lng.toFixed(6));
        addMarker(location.lat, location.lng);

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([location.lat, location.lng], 16);
        }
      } else {
        alert('Error al obtener las coordenadas de la dirección');
      }
    });
  };

  const handleApplyManualCoords = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);

    if (isNaN(lat) || isNaN(lng)) {
      alert('Por favor ingrese coordenadas válidas');
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert('Coordenadas fuera de rango válido');
      return;
    }

    setPosition({ lat, lng });
    addMarker(lat, lng);
    
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], 16);
    }
  };

  const handleSave = async () => {
    if (!position) {
      alert('Por favor seleccione una ubicación en el mapa');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = itemType === 'delivery'
        ? `${API_URL}/api/admin/delivery-point/${item.id}`
        : `${API_URL}/api/admin/order/${item.id}/address`;

      const payload = {
        address: address,
        latitude: position.lat,
        longitude: position.lng,
        district: district,
      };

      await axios.put(endpoint, payload);

      alert('Ubicación actualizada exitosamente');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Error al guardar la ubicación');
    } finally {
      setIsLoading(false);
    }
  };

  async function handleUpdateDriverLocation() {
    setIsUpdatingDriver(true);
    try {
      await axios.post(`${API_URL}/api/update_driver_location`, {
        id: item.id,
      });
      alert('Ubicación del motorizado actualizada');
    } catch (error) {
      console.error('Error updating driver location:', error);
      alert(error.response?.data?.error || 'Error al actualizar ubicación del motorizado');
    } finally {
      setIsUpdatingDriver(false);
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 font-montserrat"
      style={{ zIndex: 10000 }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[calc(100dvh-1rem)] sm:max-h-[90dvh] min-h-0 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-[#B1336E] px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-2 rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-bold text-white">
                Actualizar Ubicación
              </h2>
              <p className="text-xs text-white/80">
                {itemType === 'delivery' ? 'Entrega' : 'Recojo'} #{item.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#B1336E]/80 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">
          {/* Address Search */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={address}
                onChange={(e) => handleAddressChange(e.target.value)}
                placeholder="Buscar dirección..."
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E] focus:border-transparent"
              />
              {searchLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#B1336E]"></div>
                </div>
              )}
            </div>
            
            {/* Suggestions Dropdown */}
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-[1000]">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.place_id}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-800">{suggestion.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* District Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Distrito
            </label>
            <select
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E] focus:border-transparent bg-white"
            >
              <option value="">Seleccionar distrito...</option>
              {ALL_DISTRICTS.map((dist) => (
                <option key={dist} value={dist}>
                  {dist}
                </option>
              ))}
            </select>
          </div>

          {/* Manual Coordinates Input */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Latitud
              </label>
              <input
                type="text"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                placeholder="-12.046374"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E] focus:border-transparent text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Longitud
              </label>
              <input
                type="text"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                placeholder="-77.042793"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E] focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleApplyManualCoords}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center gap-2"
                title="Aplicar coordenadas"
              >
                <Navigation className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Map */}
          <div className="border-2 border-slate-300 rounded-lg overflow-hidden h-[350px] relative">
            {!mapReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1336E] mx-auto mb-2"></div>
                  <p className="text-sm text-slate-600">Cargando mapa...</p>
                </div>
              </div>
            )}
            <div 
              ref={mapRef} 
              className="w-full h-full"
            />
          </div>

          {/* Driver Location Update (only for deliveries with driver) */}
          {itemType === 'delivery' && item.driver_id && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-3">
                Actualizar ubicación del motorizado a esta ubicación
              </p>
              <button
                type="button"
                onClick={handleUpdateDriverLocation}
                disabled={isUpdatingDriver}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Navigation className="w-4 h-4" />
                {isUpdatingDriver ? 'Actualizando...' : 'Actualizar Ubicación Motorizado'}
              </button>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Instrucciones:</strong> Busca una dirección, ingresa coordenadas manualmente, o haz clic en el mapa para seleccionar la ubicación.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-3 py-3 sm:px-6 sm:py-4 border-t border-slate-200 rounded-b-xl flex gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!position || isLoading}
            className={`flex-1 py-2 px-4 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
              position && !isLoading
                ? 'bg-[#B1336E] hover:bg-[#B1336E]/90 text-white'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Guardando...' : 'Guardar Ubicación'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LocationModal;
